import React, { useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import { useNavigate } from 'react-router-dom';
import { formatUbicacion } from '../utils/locationUtils';
import { actasAPI } from '../services/api';
import { buildActaHistoricaUrl } from '../config';
import EstadoActaCell from './EstadoActaCell';

// Acta asociada a un activo: la de su asignación vigente o, si no hay, la
// histórica registrada para ese activo. Se consulta por activo al abrir el modal.
const ActaAsociada = ({ activo, onClose }) => {
  const navigate = useNavigate();
  const [estado, setEstado] = useState({ cargando: true, acta: null });

  useEffect(() => {
    let cancelado = false;
    (async () => {
      let acta = null;
      try {
        if (activo.asignacionId) {
          try {
            acta = (await actasAPI.getByAsignacionId(activo.asignacionId)).data;
          } catch (e) {
            if (e.response?.status !== 404) throw e;
          }
        }
        if (!acta && activo.id) {
          try {
            const { data } = await actasAPI.getActasByActivo(activo.id);
            acta = Array.isArray(data) && data.length ? data[0] : null;
          } catch {
            // Sin permiso o sin actas históricas: no es un error para el usuario.
          }
        }
      } catch (e) {
        console.error('Error al cargar el acta del activo:', e);
      }
      if (!cancelado) setEstado({ cargando: false, acta });
    })();
    return () => { cancelado = true; };
  }, [activo.asignacionId, activo.id]);

  const { cargando, acta } = estado;
  const fecha = acta?.fechaFirma || acta?.fechaCreacion;

  const verActa = () => {
    if (acta.esHistorica && acta.rutaArchivo) {
      window.open(buildActaHistoricaUrl(acta.rutaArchivo), '_blank');
    } else {
      onClose();
      navigate(`/actas/previsualizar-firmado/${acta.id}`);
    }
  };

  return (
    <div className="mt-3 pt-3 border-t border-gray-200 dark:border-gray-700">
      <h5 className="text-xs font-medium text-gray-700 dark:text-gray-300 mb-2">Acta asociada</h5>
      {cargando ? (
        <p className="text-xs text-gray-400">Buscando acta...</p>
      ) : !acta ? (
        <p className="text-xs text-gray-500 dark:text-gray-400 italic">Este activo no tiene un acta registrada.</p>
      ) : (
        <div className="flex flex-wrap items-center justify-between gap-3 rounded-lg bg-gray-50 dark:bg-gray-800/60 border border-gray-100 dark:border-gray-700 px-3 py-2.5">
          <div className="flex items-center gap-3">
            {acta.esHistorica ? (
              <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-purple-100 text-purple-700 dark:bg-purple-900/30 dark:text-purple-300">Histórica</span>
            ) : (
              <EstadoActaCell acta={acta} />
            )}
            {fecha && (
              <span className="text-xs text-gray-500 dark:text-gray-400">
                {new Date(fecha).toLocaleDateString('es-ES', { year: 'numeric', month: 'long', day: 'numeric' })}
              </span>
            )}
          </div>
          <button
            onClick={verActa}
            className="px-3 py-1.5 text-xs font-medium text-blue-700 dark:text-blue-300 border border-blue-200 dark:border-blue-800 rounded-md hover:bg-blue-50 dark:hover:bg-blue-900/20 transition-colors"
          >
            Ver acta
          </button>
        </div>
      )}
    </div>
  );
};

const ActivosAsignadosModal = ({ isOpen, onClose, usuario, activosAsignados }) => {
  if (!isOpen || !activosAsignados || activosAsignados.length === 0) return null;

  const getEstadoColor = (estado) => {
    switch (estado?.toLowerCase()) {
      case 'disponible':
        return 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200';
      case 'asignado':
        return 'bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-200';
      case 'en mantenimiento':
        return 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900 dark:text-yellow-200';
      case 'retirado':
        return 'bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-200';
      default:
        return 'bg-gray-100 text-gray-800 dark:bg-gray-700 dark:text-gray-200';
    }
  };

  const getCategoriaIcon = (categoria) => {
    switch (categoria?.toLowerCase()) {
      case 'equipos':
        return (
          <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9.75 17L9 20l-1 1h8l-1-1-.75-3M3 13h18M5 17h14a2 2 0 002-2V5a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
          </svg>
        );
      case 'móviles':
        return (
          <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 18h.01M8 21h8a2 2 0 002-2V5a2 2 0 00-2-2H8a2 2 0 00-2 2v14a2 2 0 002 2z" />
          </svg>
        );
      case 'monitores':
        return (
          <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9.75 17L9 20l-1 1h8l-1-1-.75-3M3 13h18M5 17h14a2 2 0 002-2V5a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
          </svg>
        );
      default:
        return (
          <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M20 7l-8-4-8 4m16 0l-8 4m8-4v10l-8 4m0-10L4 7m8 4v10M4 7v10l8 4" />
          </svg>
        );
    }
  };

  const parseDiscos = (discosJson) => {
    if (!discosJson) return [];
    try {
      return JSON.parse(discosJson);
    } catch {
      return [];
    }
  };

  return createPortal(
    <div
      className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-[9999]"
      onClick={(e) => {
        e.stopPropagation();
        onClose(); // Cerrar modal al hacer clic en el fondo
      }}
    >
      <div
        className="relative top-10 mx-auto p-6 md:p-8 w-full max-w-4xl shadow-2xl rounded-2xl bg-white dark:bg-gray-800 max-h-[90vh] overflow-y-auto transform transition-all"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="mt-1">
          {/* Header */}
          <div className="flex items-center justify-between mb-8 pb-4 border-b border-gray-100 dark:border-gray-700/50">
            <div className="flex items-center space-x-4">
              <div className="w-12 h-12 bg-gray-50 dark:bg-gray-800/50 rounded-xl border border-gray-100 dark:border-gray-700 flex items-center justify-center">
                <svg className="w-6 h-6 text-gray-600 dark:text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4.354a4 4 0 110 5.292M15 21H3v-1a6 6 0 0112 0v1zm0 0h6v-1a6 6 0 00-9-5.197m13.5-9a2.5 2.5 0 11-5 0 2.5 2.5 0 015 0z" />
                </svg>
              </div>
              <div>
                <h3 className="text-lg font-medium text-gray-900 dark:text-white">
                  Activos de {usuario?.nombre} {usuario?.apellido}
                </h3>
                <p className="text-sm text-gray-500 dark:text-gray-400">
                  {activosAsignados.length} activo{activosAsignados.length !== 1 ? 's' : ''} asignado{activosAsignados.length !== 1 ? 's' : ''}
                </p>
              </div>
            </div>
            <button
              onClick={(e) => {
                e.stopPropagation();
                onClose();
              }}
              className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-200"
            >
              <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>

          {/* Lista de activos */}
          <div className="space-y-6">
            {activosAsignados.map((activo, index) => (
              <div key={index} className="bg-white dark:bg-gray-800/50 border border-gray-100 dark:border-gray-700/80 rounded-xl p-5 md:p-6 shadow-sm hover:shadow-md transition-all">
                {/* Header del activo */}
                <div className="flex items-center justify-between mb-5 border-b border-gray-50 dark:border-gray-700/50 pb-4">
                  <div className="flex items-center space-x-3">
                    <div className="w-10 h-10 bg-gray-50 dark:bg-gray-800 rounded-lg border border-gray-100 dark:border-gray-700 flex items-center justify-center text-gray-600 dark:text-gray-400">
                      {getCategoriaIcon(activo.categoria)}
                    </div>
                    <div>
                      <h4 className="text-md font-medium text-gray-900 dark:text-white">
                        {activo.nombreEquipo || activo.codigo}
                      </h4>
                      <p className="text-sm text-gray-500 dark:text-gray-400">
                        {activo.categoria}
                      </p>
                    </div>
                  </div>
                  <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${getEstadoColor(activo.estado)}`}>
                    {activo.estado}
                  </span>
                </div>

                {/* Información del activo */}
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                  <div>
                    <h5 className="text-sm font-semibold text-gray-700 dark:text-gray-300 mb-4 flex items-center">
                      <svg className="w-4 h-4 mr-2 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M20 7l-8-4-8 4m16 0l-8 4m8-4v10l-8 4m0-10L4 7m8 4v10M4 7v10l8 4" />
                      </svg>
                      Información General
                    </h5>
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                      <div>
                        <span className="text-xs font-medium text-gray-500 dark:text-gray-400">Código:</span>
                        <p className="text-sm text-gray-900 dark:text-white">{activo.codigo}</p>
                      </div>
                      <div>
                        <span className="text-xs font-medium text-gray-500 dark:text-gray-400">Categoría:</span>
                        <p className="text-sm text-gray-900 dark:text-white">{activo.categoria}</p>
                      </div>
                      {activo.ubicacion && (
                        <div>
                          <span className="text-xs font-medium text-gray-500 dark:text-gray-400">Ubicación:</span>
                          <p className="text-sm text-gray-900 dark:text-white">{formatUbicacion(activo.ubicacion)}</p>
                        </div>
                      )}
                      {activo.nombreEquipo && (
                        <div>
                          <span className="text-xs font-medium text-gray-500 dark:text-gray-400">Nombre del Equipo:</span>
                          <p className="text-sm text-gray-900 dark:text-white">{activo.nombreEquipo}</p>
                        </div>
                      )}
                      {activo.tipoEquipo && (
                        <div>
                          <span className="text-xs font-medium text-gray-500 dark:text-gray-400">Tipo de Equipo:</span>
                          <p className="text-sm text-gray-900 dark:text-white">{activo.tipoEquipo}</p>
                        </div>
                      )}
                    </div>
                  </div>

                  <div>
                    <h5 className="text-sm font-semibold text-gray-700 dark:text-gray-300 mb-4 flex items-center">
                      <svg className="w-4 h-4 mr-2 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 3v2m6-2v2M9 19v2m6-2v2M5 9H3m2 6H3m18-6h-2m2 6h-2M7 19h10a2 2 0 002-2V7a2 2 0 00-2-2H7a2 2 0 00-2 2v10a2 2 0 002 2zM9 9h6v6H9V9z" />
                      </svg>
                      Especificaciones Técnicas
                    </h5>
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                      {activo.procesador && (
                        <div>
                          <span className="text-xs font-medium text-gray-500 dark:text-gray-400">Procesador:</span>
                          <p className="text-sm text-gray-900 dark:text-white">{activo.procesador}</p>
                        </div>
                      )}
                      {activo.sistemaOperativo && (
                        <div>
                          <span className="text-xs font-medium text-gray-500 dark:text-gray-400">Sistema Operativo:</span>
                          <p className="text-sm text-gray-900 dark:text-white">{activo.sistemaOperativo}</p>
                        </div>
                      )}
                      {activo.ram && (
                        <div>
                          <span className="text-xs font-medium text-gray-500 dark:text-gray-400">RAM:</span>
                          <p className="text-sm text-gray-900 dark:text-white">{activo.ram}</p>
                        </div>
                      )}
                      {activo.almacenamiento && (
                        <div>
                          <span className="text-xs font-medium text-gray-500 dark:text-gray-400">Almacenamiento:</span>
                          <p className="text-sm text-gray-900 dark:text-white">{activo.almacenamiento}</p>
                        </div>
                      )}
                      {activo.marca && (
                        <div>
                          <span className="text-xs font-medium text-gray-500 dark:text-gray-400">Marca:</span>
                          <p className="text-sm text-gray-900 dark:text-white">{activo.marca}</p>
                        </div>
                      )}
                      {activo.modelo && (
                        <div>
                          <span className="text-xs font-medium text-gray-500 dark:text-gray-400">Modelo:</span>
                          <p className="text-sm text-gray-900 dark:text-white">{activo.modelo}</p>
                        </div>
                      )}
                      {activo.serie && (
                        <div>
                          <span className="text-xs font-medium text-gray-500 dark:text-gray-400">N° Serie:</span>
                          <p className="text-sm text-gray-900 dark:text-white">{activo.serie}</p>
                        </div>
                      )}
                      {activo.pulgadas && (
                        <div>
                          <span className="text-xs font-medium text-gray-500 dark:text-gray-400">Pulgadas:</span>
                          <p className="text-sm text-gray-900 dark:text-white">{activo.pulgadas}</p>
                        </div>
                      )}
                      {activo.imei && (
                        <div>
                          <span className="text-xs font-medium text-gray-500 dark:text-gray-400">IMEI:</span>
                          <p className="text-sm text-gray-900 dark:text-white">{activo.imei}</p>
                        </div>
                      )}
                    </div>
                  </div>
                </div>

                {/* Observaciones */}
                <div className="mt-4 bg-gray-50 dark:bg-gray-700/50 rounded-lg p-4">
                  <h5 className="text-sm font-semibold text-gray-700 dark:text-gray-300 mb-2 flex items-center">
                    <svg className="w-4 h-4 mr-2 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                    </svg>
                    Observaciones
                  </h5>
                  <div className="text-sm text-gray-600 dark:text-gray-300 whitespace-pre-wrap">
                    {activo.observaciones || <span className="text-gray-400 italic">Sin observaciones registradas.</span>}
                  </div>
                </div>

                {/* Discos (si aplica) */}
                {activo.discosJson && parseDiscos(activo.discosJson).length > 0 && (
                  <div className="mt-3">
                    <h5 className="text-xs font-medium text-gray-700 dark:text-gray-300 mb-2">
                      Discos
                    </h5>
                    <div className="space-y-1">
                      {parseDiscos(activo.discosJson).map((disco, discoIndex) => (
                        <div key={discoIndex} className="bg-gray-50 dark:bg-gray-700 rounded p-2">
                          <p className="text-xs text-gray-900 dark:text-white">
                            <span className="font-medium">Disco {discoIndex + 1}:</span> {disco.tipo} {disco.capacidad}GB
                          </p>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                <ActaAsociada activo={activo} onClose={onClose} />

                {/* Fecha de Asignación */}
                {activo.fechaAsignacion && (
                  <div className="mt-3 pt-3 border-t border-gray-200 dark:border-gray-700">
                    <h5 className="text-xs font-medium text-gray-700 dark:text-gray-300 mb-1">
                      Fecha de Asignación
                    </h5>
                    <p className="text-xs text-gray-900 dark:text-white">
                      {new Date(activo.fechaAsignacion).toLocaleDateString('es-ES', {
                        year: 'numeric',
                        month: 'long',
                        day: 'numeric',
                        hour: '2-digit',
                        minute: '2-digit'
                      })}
                    </p>
                  </div>
                )}
              </div>
            ))}
          </div>

          {/* Botones */}
          <div className="flex justify-end space-x-3 mt-6">
            <button
              onClick={onClose}
              className="px-4 py-2 text-sm font-medium text-gray-700 dark:text-gray-300 bg-gray-100 dark:bg-gray-700 rounded-md hover:bg-gray-200 dark:hover:bg-gray-600"
            >
              Cerrar
            </button>
          </div>
        </div>
      </div>
    </div>,
    document.body
  );
};

export default ActivosAsignadosModal; 